import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";

import {
  EVENT_MODELS,
  fillTemplate,
  formatAmount,
  getCrewSizeFee,
} from "./crewSizeUtils";

const Box = ({ children }) => (
  <section className="crew-size-step__box" aria-live="polite">
    {children}
  </section>
);

Box.propTypes = {
  children: PropTypes.node.isRequired,
};

const CrewFeeSummary = ({
  t,
  selectedOption,
  model,
  planName,
  operatingDays,
  customIncomplete,
}) => {
  // Custom fields partly filled or invalid: nothing to calculate yet.
  if (customIncomplete) {
    return (
      <Box>
        <Typography component="p" className="text-label-md crew-size-step__text">
          {t.estimatedFee}
        </Typography>
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {t.custom.enterToSeeFee}
        </Typography>
      </Box>
    );
  }

  if (!selectedOption) {
    return (
      <Box>
        <Typography component="p" className="text-label-md crew-size-step__text">
          {t.emptyLabel}
        </Typography>
        <Typography component="h2" className="text-heading-lg crew-size-step__highlight">
          {t.emptyTitle}
        </Typography>
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {t.emptyDescription}
        </Typography>
      </Box>
    );
  }

  const isExtended = model === EVENT_MODELS.extended;

  // The step prices a custom plan: the backend's cost once saved, live while the admin types.
  if (selectedOption.custom) {
    return (
      <Box>
        <Typography component="p" className="text-label-md crew-size-step__text">
          {t.estimatedFee}
        </Typography>
        <Typography component="h2" className="text-display-sm crew-size-step__highlight">
          {selectedOption.cost != null
            ? `${formatAmount(selectedOption.cost)} ${t.currency}`
            : "—"}
        </Typography>
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {isExtended
            ? t.custom.extendedBreakdown
            : fillTemplate(t.custom.dailyBreakdown, {
                rate: formatAmount(selectedOption.rate),
                days: formatAmount(operatingDays),
              })}
        </Typography>
      </Box>
    );
  }

  // Standard sizes are priced by the backend (`cost`, size fee); nothing is computed here.
  const fee = getCrewSizeFee(selectedOption, model);

  return (
    <Box>
      <Typography component="p" className="text-label-md crew-size-step__text">
        {t.estimatedFee}
      </Typography>
      <Typography component="h2" className="text-display-sm crew-size-step__highlight">
        {selectedOption.cost != null
          ? `${formatAmount(selectedOption.cost)} ${t.currency}`
          : "—"}
      </Typography>
      {fee != null && (
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {fillTemplate(isExtended ? t.extendedCalculation : t.dailyCalculation, {
            fee: formatAmount(fee),
            days: formatAmount(operatingDays),
          })}
        </Typography>
      )}
      <Typography component="p" className="text-body-sm crew-size-step__text">
        {[
          isExtended ? t.extendedModel : t.dailyModel,
          planName ? fillTemplate(t.planRate, { plan: planName }) : null,
          t.recalculates,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
    </Box>
  );
};

CrewFeeSummary.propTypes = {
  t: PropTypes.object.isRequired,
  selectedOption: PropTypes.shape({
    max: PropTypes.number,
    /** Fees of this size per day / flat, as priced by the backend. */
    dailyFee: PropTypes.number,
    monthlyFee: PropTypes.number,
    /** Fee of this size for the event, as priced by the backend. */
    cost: PropTypes.number,
    /** Custom plan: carries its own agreed rate. */
    custom: PropTypes.bool,
    rate: PropTypes.number,
  }),
  /** Daily or extended billing of the event. */
  model: PropTypes.string.isRequired,
  /** Subscription plan of the company, e.g. "Basic". */
  planName: PropTypes.string,
  operatingDays: PropTypes.number,
  /** The Custom fields are partly filled or invalid. */
  customIncomplete: PropTypes.bool,
};

CrewFeeSummary.defaultProps = {
  selectedOption: null,
  planName: "",
  operatingDays: 0,
  customIncomplete: false,
};

export default CrewFeeSummary;
